import React, { useState, useRef, useEffect } from 'react';
import { Bluetooth, Printer, Scissors, RefreshCw, Type, AlertCircle } from 'lucide-react';

const App = () => {
    const [device, setDevice] = useState(null);
    const [characteristic, setCharacteristic] = useState(null);
    const [status, setStatus] = useState('Disconnected');
    const [text, setText] = useState('YHK Printer Test\nReady to Print!');
    const [isPrinting, setIsPrinting] = useState(false);
    const canvasRef = useRef(null);

    const PRINTER_NAME = 'YHK-BDF8';
    const PRINTER_WIDTH = 384;

    const COMMON_SERVICES = [
        '0000ff00-0000-1000-8000-00805f9b34fb',
        '0000ae00-0000-1000-8000-00805f9b34fb',
        '000018f0-0000-1000-8000-00805f9b34fb',
        '49535343-fe7d-4ae5-8fa9-9fafd205e455'
    ];

    const connect = async () => {
        try {
            setStatus('Searching...');
            const dev = await navigator.bluetooth.requestDevice({
                filters: [{ name: PRINTER_NAME }],
                optionalServices: COMMON_SERVICES
            });

            setDevice(dev);
            dev.addEventListener('gattserverdisconnected', onDisconnected);

            setStatus('Connecting...');
            const server = await dev.gatt.connect();

            let foundChar = null;
            for (const serviceUuid of COMMON_SERVICES) {
                try {
                    const service = await server.getPrimaryService(serviceUuid);
                    const characteristics = await service.getCharacteristics();
                    foundChar = characteristics.find(c => c.properties.write || c.properties.writeWithoutResponse);
                    if (foundChar) break;
                } catch (e) { continue; }
            }

            if (!foundChar) throw new Error("No writable service found.");

            setCharacteristic(foundChar);
            setStatus('Connected');
        } catch (err) {
            console.error(err);
            setStatus('Error: ' + err.message);
        }
    };

    const onDisconnected = () => {
        setDevice(null);
        setCharacteristic(null);
        setStatus('Disconnected');
    };

    const disconnect = () => {
        if (device && device.gatt.connected) device.gatt.disconnect();
    };

    const processCanvas = () => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const pixels = imgData.data;
        const widthInBytes = Math.ceil(canvas.width / 8);
        const data = new Uint8Array(widthInBytes * canvas.height);

        let lastActiveRow = 0;

        for (let y = 0; y < canvas.height; y++) {
            let rowHasData = false;
            for (let x = 0; x < canvas.width; x++) {
                const idx = (y * canvas.width + x) * 4;
                const avg = (pixels[idx] + pixels[idx + 1] + pixels[idx + 2]) / 3;
                if (avg < 160) {
                    const byteIdx = y * widthInBytes + Math.floor(x / 8);
                    const bitIdx = 7 - (x % 8);
                    data[byteIdx] |= (1 << bitIdx);
                    rowHasData = true;
                }
            }
            if (rowHasData) lastActiveRow = y;
        }

        // Trim unused height to avoid excessive paper feed at the end
        return {
            data: data.slice(0, (lastActiveRow + 1) * widthInBytes),
            height: lastActiveRow + 1
        };
    };

    const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

    const printAction = async () => {
        if (!characteristic) return;
        setIsPrinting(true);
        setStatus('Printing...');

        try {
            // 1. Initialize
            await characteristic.writeValue(new Uint8Array([0x1B, 0x40]));
            await sleep(50);

            updateCanvas();
            const { data: bitmap, height } = processCanvas();
            const bytesPerRow = PRINTER_WIDTH / 8;

            // 2. Optimized Line-by-line printing
            // Instead of 1 row at a time, we can try small blocks of 5-10 rows 
            // to speed up the process while still giving the motor room.
            const ROWS_PER_PACKET = 8;

            for (let y = 0; y < height; y += ROWS_PER_PACKET) {
                const rowsToPrint = Math.min(ROWS_PER_PACKET, height - y);
                const rowOffset = y * bytesPerRow;
                const rowData = bitmap.slice(rowOffset, rowOffset + (rowsToPrint * bytesPerRow));

                // Command: GS v 0 0 xL xH yL yH
                const xL = bytesPerRow % 256;
                const xH = Math.floor(bytesPerRow / 256);
                const yL = rowsToPrint % 256;
                const yH = Math.floor(rowsToPrint / 256);

                const lineHeader = new Uint8Array([0x1D, 0x76, 0x30, 0, xL, xH, yL, yH]);
                const packet = new Uint8Array(lineHeader.length + rowData.length);
                packet.set(lineHeader);
                packet.set(rowData, lineHeader.length);

                // Send packet (split into BLE MTU chunks if needed)
                const CHUNK_SIZE = 20;
                for (let i = 0; i < packet.length; i += CHUNK_SIZE) {
                    await characteristic.writeValue(packet.slice(i, i + CHUNK_SIZE));
                }

                // Reduced delay for faster progression
                await sleep(5);

                if (y % 40 === 0) {
                    setStatus(`Printing: ${Math.round((y / height) * 100)}%`);
                }
            }

            // 3. Final Feed
            await sleep(100);
            // Small feed to push text past the tear bar
            await characteristic.writeValue(new Uint8Array([0x1B, 0x4A, 0x60]));

            setStatus('Connected');
        } catch (err) {
            console.error("Print Error:", err);
            setStatus('Print Failed');
        } finally {
            setIsPrinting(false);
        }
    };

    const updateCanvas = () => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        const lines = text.split('\n');

        // Tighter height calculation
        canvas.height = Math.max(60, lines.length * 40 + 20);

        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        ctx.fillStyle = 'black';
        ctx.font = 'bold 30px "Courier New", monospace';
        ctx.textAlign = 'center';
        lines.forEach((line, i) => {
            ctx.fillText(line, canvas.width / 2, 40 + i * 40);
        });
    };

    useEffect(() => {
        updateCanvas();
    }, [text]);

    return (
        <div className="min-h-screen bg-zinc-100 p-6 flex flex-col items-center font-sans">
            <div className="w-full max-w-md space-y-6">

                <div className="bg-white rounded-3xl p-6 shadow-xl border border-zinc-200">
                    <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center gap-2">
                            <div className="bg-blue-600 p-2 rounded-lg text-white">
                                <Printer size={20} />
                            </div>
                            <h1 className="font-bold text-xl text-zinc-800">Dr House print</h1>
                        </div>
                        <div className={`text-[10px] uppercase font-black px-2 py-1 rounded border ${status === 'Connected' ? 'border-green-500 text-green-500' : 'border-zinc-300 text-zinc-400'
                            }`}>
                            {status}
                        </div>
                    </div>

                    {!characteristic ? (
                        <button
                            onClick={connect}
                            className="w-full py-4 bg-zinc-900 text-white rounded-2xl font-bold flex items-center justify-center gap-3 active:scale-95 transition-transform"
                        >
                            <Bluetooth size={20} />
                            Pair Printer
                        </button>
                    ) : (
                        <div className="space-y-4">
                            <div className="bg-zinc-50 p-4 rounded-xl border border-zinc-100">
                                <label className="text-[10px] font-bold text-zinc-400 uppercase block mb-2">Message</label>
                                <textarea
                                    value={text}
                                    onChange={(e) => setText(e.target.value)}
                                    className="w-full bg-transparent text-lg focus:outline-none resize-none h-24"
                                />
                            </div>

                            <div className="flex gap-3">
                                <button
                                    onClick={printAction}
                                    disabled={isPrinting}
                                    className="flex-1 py-4 bg-blue-600 text-white rounded-2xl font-bold flex items-center justify-center gap-2 shadow-lg shadow-blue-100 active:scale-95 transition-all disabled:bg-zinc-300"
                                >
                                    {isPrinting ? <RefreshCw className="animate-spin" /> : <Printer size={18} />}
                                    {isPrinting ? 'Fast Print' : 'Print'}
                                </button>
                                <button
                                    onClick={disconnect}
                                    className="px-4 bg-zinc-100 text-zinc-400 rounded-2xl hover:text-red-500 transition-colors"
                                >
                                    <RefreshCw size={18} />
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                <div className="flex flex-col items-center opacity-40">
                    <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 mb-2">Canvas Content</p>
                    <div className="bg-white p-2 rounded-lg border border-zinc-200">
                        <canvas ref={canvasRef} width={PRINTER_WIDTH} className="w-48 shadow-inner" />
                    </div>
                </div>

                {status.includes('Error') && (
                    <div className="bg-red-50 border border-red-100 p-4 rounded-2xl flex items-start gap-3 text-red-600">
                        <AlertCircle className="shrink-0" size={18} />
                        <p className="text-xs font-medium leading-relaxed">{status}</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default App;